import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { usePlatform } from '../context/PlatformContext';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { depositIntent, depositConfirm } from '../utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({ grossCents, depositCents, onSuccess, onError }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [ready, setReady]     = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setLoading(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements, redirect: 'if_required'
      });
      if (error) onError(error.message);
      else if (paymentIntent.status === 'succeeded') {
        await depositConfirm(paymentIntent.id);
        onSuccess(depositCents);
      }
    } catch (err) { onError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={s.stripeWrap}>
        <PaymentElement onReady={() => setReady(true)}/>
      </div>
      <button style={{...s.btn, opacity:(!stripe||!ready) ? 0.5 : 1}}
        type="submit" disabled={loading||!stripe||!ready}>
        {loading ? 'Processing...' : !ready ? 'Loading...' : 'Pay $'+(grossCents/100).toFixed(2)}
      </button>
    </form>
  );
}

export default function BuyPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Add Funds';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const [amount, setAmount]               = useState('');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState(null);
  const [clientSecret, setClientSecret]   = useState(null);
  const [intentGross, setIntentGross]     = useState(0);
  const platform = usePlatform();
  const [intentDeposit, setIntentDeposit] = useState(0);

  const depositCents = Math.round(parseFloat(amount||0) * 100);
  const feeCents     = Math.round(depositCents * (platform.feeBps / 10000));
  const grossCents   = depositCents + feeCents;

  async function handleContinue() {
    if (depositCents < platform.minDepositCents) { setError(platform.minDepositCents > 0 ? `Minimum deposit is $${(platform.minDepositCents/100).toFixed(2)}` : 'Please enter an amount'); return; }
    setError(''); setLoading(true);
    try {
      const res = await depositIntent(grossCents);
      setClientSecret(res.data.clientSecret);
      setIntentGross(grossCents);
      setIntentDeposit(depositCents);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment');
    } finally { setLoading(false); }
  }

  if (success) return (
    <div style={s.page}>
      <div style={s.successWrap}>
        <div style={s.successIcon}>✓</div>
        <div style={s.successTitle}>${(success/100).toFixed(2)} added</div>
        <div style={s.successSub}>Your balance has been updated</div>
        <button style={s.btn} onClick={() => navigate('/dashboard')}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back}
          onClick={() => clientSecret ? setClientSecret(null) : navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Add Funds</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        {!clientSecret ? (
          <>
            <div style={s.amountCard}>
              <label style={s.fieldLabel}>Amount to add to your wallet</label>
              <div style={s.amountRow}>
                <span style={s.dollar}>$</span>
                <input style={s.amountInput} type="number" placeholder="0.00"
                  min="1" step="0.01" value={amount}
                  onChange={e => { setAmount(e.target.value); setError(''); }}/>
              </div>
            </div>

            <div style={s.quickRow}>
              {[5,10,20,50].map(v => (
                <button key={v}
                  style={{...s.quickBtn, ...(Number(amount)===v ? s.quickBtnActive : {})}}
                  onClick={() => setAmount(String(v))}>${v}</button>
              ))}
            </div>

            <div style={s.summaryCard}>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>Added to your balance</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#00C48C'}}>
                  ${(depositCents/100).toFixed(2)}
                </span>
              </div>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>{`Service fee (${platform.feePct}%)`}</span>
                <span style={s.summaryValue}>+${(feeCents/100).toFixed(2)}</span>
              </div>
              <div style={s.divider}/>
              <div style={s.summaryRow}>
                <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>Total charged</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>
                  ${(grossCents/100).toFixed(2)}
                </span>
              </div>
            </div>

            <div style={s.noteCard}>
              <span style={s.noteIcon}>i</span>
              <span style={s.noteText}>
                You type what you want in your wallet. {`The ${platform.feePct}% fee is added on top`}.
              </span>
            </div>

            {error && <div style={s.error}>{error}</div>}

            <button style={s.btn} onClick={handleContinue} disabled={loading}>
              {loading ? 'Loading...' : 'Continue — pay $'+(grossCents/100).toFixed(2)}
            </button>
          </>
        ) : (
          <>
            <div style={s.summaryCard}>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>Added to your balance</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#00C48C'}}>
                  ${(intentDeposit/100).toFixed(2)}
                </span>
              </div>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>{`Service fee (${platform.feePct}%)`}</span>
                <span style={s.summaryValue}>
                  +${((intentGross-intentDeposit)/100).toFixed(2)}
                </span>
              </div>
              <div style={s.divider}/>
              <div style={s.summaryRow}>
                <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>Total charged</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>
                  ${(intentGross/100).toFixed(2)}
                </span>
              </div>
            </div>

            <Elements stripe={stripePromise} options={{clientSecret}}>
              <CheckoutForm grossCents={intentGross} depositCents={intentDeposit}
                onSuccess={c => setSuccess(c)} onError={m => setError(m)}/>
            </Elements>

            {error && <div style={s.error}>{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:        {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:          {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:   {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:          {padding:'0 16px'},
  amountCard:    {background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  fieldLabel:    {fontSize:12, fontWeight:500, color:'#6E6E73', display:'block', marginBottom:8},
  amountRow:     {display:'flex', alignItems:'center', gap:4},
  dollar:        {fontSize:36, fontWeight:600, color:'#1D1D1F'},
  amountInput:   {fontSize:36, fontWeight:600, border:'none', background:'transparent', width:'100%', outline:'none', color:'#1D1D1F'},
  quickRow:      {display:'flex', gap:8, marginBottom:12},
  quickBtn:      {flex:1, padding:'10px 0', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, fontWeight:500, color:'#1D1D1F'},
  quickBtnActive:{borderColor:'#007AFF', color:'#007AFF', background:'#EBF4FF'},
  summaryCard:   {background:'#fff', borderRadius:16, padding:16, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  summaryRow:    {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0'},
  summaryLabel:  {fontSize:14, color:'#6E6E73'},
  summaryValue:  {fontSize:14, color:'#6E6E73'},
  divider:       {height:1, background:'#F5F5F7', margin:'10px 0'},
  noteCard:      {background:'#EBF4FF', borderRadius:12, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:8, marginBottom:16},
  noteIcon:      {fontSize:13, fontWeight:700, color:'#007AFF', flexShrink:0},
  noteText:      {fontSize:12, color:'#007AFF', lineHeight:1.5},
  stripeWrap:    {background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  error:         {background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12},
  btn:           {width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3},
  successWrap:   {display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:24, gap:12, textAlign:'center'},
  successIcon:   {width:64, height:64, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28},
  successTitle:  {fontSize:28, fontWeight:700, letterSpacing:-1},
  successSub:    {fontSize:14, color:'#6E6E73'}
};
